import React, { useCallback, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackAddToCart } from 'store/addToCart/helpers';
import { fetchVariants } from 'store/variants/action';
import {
  getWidgetTrackingValues,
  handleAddToCart,
  handleAddToWishlist,
  isListingPage,
} from './helper';
import {
  DEFAULT_LANG,
  PRODUCT_CARD_ACTION,
  ROOT,
  SELECT,
  TRACKING_EVENTS,
  VARIANT_TYPE,
} from './constants';
import { useHistory } from 'react-router-dom';
import { SNACKBAR_ID, WISHLIST_SNACKBAR_ID } from './constants';
import { CartData } from 'store/addToCart/types';
import { getPageLocation } from 'store/categoryListing/helpers/getPageLocation';
import { State } from 'store/types';
import { ProductWidgetParams } from '@nykaa/deals-widgets/components/ProductV2Child/interface';
import { productCardAction } from 'helpers/utils/vernacular/tracking';
import NotifyMe from 'components/NotifyMeModal';
import Snackbar from 'components/Snackbar';
import AddToBagToast from 'components/AddToBagToast';
import AllVariant from 'components/Recommendations/AllVariants';
import { sendSingleEvent } from 'shared/helpers/impressions/sendSingleRetinaEvent';
import { isShade } from 'shared/Nps/shared/helpers';
import {
  PRODUCT_ADD_TO_CART,
  CLICK,
  PRODUCT_SELECT_SIZE,
  PRODUCT_SELECT_SHADE,
  PRODUCT_NOTIFY_ME,
} from '@nykaa/utils/retina/constants';
import { PageJourney } from 'store/dataLayer/action';
import { SiteNavigation } from 'constants/dataLayer';
import { Device } from '@nykaa/data-layer/constants';
import { UserStoreState } from 'User/types';

interface VariantState {
  display: boolean;
  product: any; // Mispatch of the product type is used for All Variant Component
  siteNavigation?: string;
  siteSubNavigation?: string;
}

export interface ProductWidgetTrackingProps {
  isProductWidget: boolean;
  device: Device;
  tierName: string;
}

const useCartOperations = (pageName?: string) => {
  const user = useSelector((state: State) => state?.auth?.user);
  const tierName = useSelector(
    (state: UserStoreState) =>
      state.loyaltyReducer?.loyaltyData?.tierName || 'non_prive'
  );
  const dispatch = useDispatch();
  const userEmail = useSelector((state: State) => state?.auth.user.email);
  const variantsMap = useSelector((state: State) => state?.variants.data);

  const device = useSelector((state: State) => state.appReducer?.client.device);
  const isProductWidget = !!(device === 1 || device === 0);
  const productWidgetTrackingProps = {
    isProductWidget,
    device,
    tierName,
  };
  // wishlist
  const [wishlistMessage, setWishlistMessage] = useState('');
  const [wishlistError, setWishlistError] = useState(false);

  // add to bag
  const [addToBagMessage, setAddToBagMessage] = useState('');
  const [addToBagError, setAddToBagError] = useState(false);

  const { wishlist } = user;

  const [notifyMe, setNotifyMe] = useState<VariantState>({
    display: false,
    product: {},
  });

  const [variant, setVariant] = useState<VariantState>({
    display: false,
    product: {},
    siteNavigation: SiteNavigation.PRODUCT_WIDGETS,
    siteSubNavigation: `${SiteNavigation.PRODUCT_WIDGETS}:variant`,
  });
  const categoryId = useSelector(
    (state: State) => state.dataLayer?.category?.id
  );
  const listingPageType = useSelector(
    (state: State) => state.dataLayer.listingPageType
  );
  let suggestionType = useSelector(
    (state: State) => state.searchListingPage.activeFilters?.suggestionType
  );
  const listingOpenedFrom = useSelector(
    (state: State) => state?.dataLayer?.listingOpenedFrom
  );
  const clickInteraction = isListingPage(pageName) && listingOpenedFrom;
  const { product } = notifyMe;
  const history = useHistory();
  const { search: queryString } = history.location;
  const queryParams = new URLSearchParams(queryString);
  let root = queryParams.get(ROOT);
  if (listingPageType) {
    suggestionType = listingPageType;
  }
  const handleAddToCartTracking = useCallback(
    async (
      cartData: CartData,
      selectedProduct?: ProductWidgetParams,
      journeyData?: PageJourney
    ) => {
      const currentSku = {
        ...selectedProduct,
        name: selectedProduct?.name,
        price: selectedProduct?.salePrice,
        variantType: selectedProduct?.variantType,
        discount: selectedProduct?.discountPercent,
        parentId: selectedProduct?.id,
        childId: selectedProduct?.id || selectedProduct?.childId,
        inStock: selectedProduct?.inStock,
        onlyWishlistButton: selectedProduct?.onlyWishlistButton,
        isLux: selectedProduct?.isLux,
        offerMessage: selectedProduct?.message,
        primaryCategories: selectedProduct?.primaryCategories,
        brandName: selectedProduct?.brandName,
        offerPrice: selectedProduct?.discountedPrice,
      };
      await trackAddToCart(
        {
          product: currentSku,
          cartData,
          pageSource: selectedProduct?.pageSource,
          trackingData: `${selectedProduct?.trackingParams}:${TRACKING_EVENTS.ADD_TO_CART}`,
          clickInteraction,
          journeyData,
        },
        productWidgetTrackingProps
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clickInteraction]
  );

  // Variant Case Handling
  const handleVariantClick = (
    selectedProduct: ProductWidgetParams,
    dnTrackingData: Record<string, any>
  ) => {
    const { siteNavigation = '', siteSubNavigation = '' } =
      getWidgetTrackingValues(dnTrackingData);
    const isWeb = !!(device === 2);
    setVariant((prev) => ({
      ...prev,
      product: selectedProduct,
      display: isWeb,
      siteNavigation,
      siteSubNavigation,
    }));
    !isShade(selectedProduct?.variantType)
      ? sendSingleEvent({
          ...dnTrackingData,
          ...{
            event_type: CLICK,
            event_name: PRODUCT_SELECT_SIZE,
          },
        })
      : sendSingleEvent({
          ...dnTrackingData,
          ...{
            event_type: CLICK,
            event_name: PRODUCT_SELECT_SHADE,
          },
        });
  };
  const handleVariantTracking = (
    selectedProduct: ProductWidgetParams,
    dnTrackingData: Record<string, any>
  ) => {
    const { interactionLocation = '' } =
      getWidgetTrackingValues(dnTrackingData);
    const trackingOptions = {
      eventName: PRODUCT_CARD_ACTION,
      isExperimentEnabled: true,
      product: selectedProduct,
      clickedFrom: pageName,
      lang: DEFAULT_LANG,
      action: selectedProduct.variantType,
      showOffer: true,
      trackingData: `${selectedProduct?.trackingParams}:${SELECT}${selectedProduct?.variantType}`,
      clickInteraction,
      interactionLocation,
    };
    productCardAction(trackingOptions);
  };
  const handleVariants = (
    selectedProduct: ProductWidgetParams,
    dnTrackingData: Record<string, any>
  ) => {
    handleVariantClick(selectedProduct, dnTrackingData);
    handleVariantTracking(selectedProduct, dnTrackingData);
  };
  // NotifyMe Handling
  const handleNotifyMeClick = (
    selectedProduct: ProductWidgetParams,
    dnTrackingData: Record<string, any>
  ) => {
    setNotifyMe((prev) => ({
      ...prev,
      product: selectedProduct,
      display: true,
    }));
    sendSingleEvent({
      ...dnTrackingData,
      event_type: CLICK,
      event_name: PRODUCT_NOTIFY_ME,
    });
  };

  // Add to Cart, NotifyMe , Variant Case and click tracking
  const addToCartHandler = (
    selectedProduct: ProductWidgetParams,
    setIsLoading: Function,
    dnTrackingData: Record<string, any>
  ) => {
    const isVariantsAvailable = !!selectedProduct?.inStock;
    const isPro = !!user?.isPro;
    const payload = {
      product: selectedProduct,
      isVariantsAvailable,
      setIsLoading,
      dispatch,
      isPro,
      setAddToBagError,
      setAddToBagMessage,
      handleAddToCartTracking,
      dnTrackingData: {
        ...dnTrackingData,
        event_type: CLICK,
        event_name: PRODUCT_ADD_TO_CART,
      },
    };
    if (
      selectedProduct.variantType === VARIANT_TYPE.SHADE ||
      selectedProduct.variantType === VARIANT_TYPE.SIZE
    ) {
      handleVariants(selectedProduct, dnTrackingData);
    } else if (!selectedProduct.inStock) {
      handleNotifyMeClick(selectedProduct, dnTrackingData);
    } else {
      handleAddToCart(payload);
    }
  };

  // Wishlist click add and remove handling along with tracking
  const addToWishlistHandler = (
    selectedProduct: ProductWidgetParams,
    isInWishlist: boolean,
    dnTrackingData: Record<string, any>
  ) => {
    const payload = {
      setWishlistMessage,
      setWishlistError,
      dnTrackingData,
      product: {
        ...selectedProduct,
        mrp: selectedProduct.salePrice,
        offerPrice: selectedProduct.discountedPrice,
        parentId: selectedProduct.id,
        isLux: selectedProduct?.isLux,
        discount: selectedProduct?.discountPercent,
      },
      dispatch,
      brandName: selectedProduct.brandName,
      pageLocation: {
        pageType: pageName,
      },
      pwPageSource: selectedProduct?.pageSource,
      primaryCategories: selectedProduct.primaryCategories,
      videoId: 'videoId',
      user,
      email: userEmail,
      isInWishlist,
      trackingData: `${selectedProduct?.trackingParams}:${
        isInWishlist
          ? TRACKING_EVENTS.REMOVE_FROM_WISHLIST
          : TRACKING_EVENTS.ADD_TO_WISHLIST
      }`,
      clickInteraction,
    };
    handleAddToWishlist(payload, productWidgetTrackingProps);
  };
  //
  const handleWishListSnackBarClose = () => {
    setWishlistMessage('');
  };

  const handleAddtoBagSnackBarClose = () => {
    setAddToBagMessage('');
  };

  const handleVariantClose = () => {
    setVariant((prev) => ({
      ...prev,
      display: false,
    }));
  };

  const handleNotifyMeClose = () => {
    setNotifyMe((prev) => ({
      ...prev,
      display: false,
    }));
  };

  // fetch the varaints for the selected product.
  const handleFetchVariants = useCallback(
    async (params: any) => await dispatch(fetchVariants(params)),
    [dispatch]
  );

  //  Cases after the click of cta button
  const CartOperations = useCallback(() => {
    const pageLocation = getPageLocation({ root, categoryId, suggestionType });
    const journeyData = {
      siteNavigation: variant.siteNavigation,
      siteSubNavigation: variant.siteSubNavigation,
    };
    return (
      <Fragment>
        <AddToBagToast
          errorType={addToBagError}
          message={addToBagMessage}
          id={SNACKBAR_ID}
          closeCallback={handleAddtoBagSnackBarClose}
          device={device}
        />
        {wishlistMessage && (
          <Snackbar
            autoClose
            closeCallback={handleWishListSnackBarClose}
            content={wishlistMessage}
            errorType={wishlistError}
            snackBarId={WISHLIST_SNACKBAR_ID}
          />
        )}
        {notifyMe.display && (
          <NotifyMe
            productId={product.id}
            offerPrice={product.salePrice}
            brandName={product.brandName}
            productName={product.title}
            primaryCategories={product?.primaryCategories}
            isLux={product?.isLux}
            pageSource={product.pageSource}
            onClose={handleNotifyMeClose}
            trackingData={`${product?.trackingParams}:${TRACKING_EVENTS.NOTIFY_ME}`}
            product={product}
          />
        )}
        {variant.display && (
          <AllVariant
            product={variant.product}
            variantsMap={variantsMap}
            onCloseHandler={handleVariantClose}
            fetchVariants={handleFetchVariants}
            handleAddToCartTracking={handleAddToCartTracking}
            pageLocation={pageLocation}
            journeyData={journeyData}
          />
        )}
      </Fragment>
    );
  }, [
    root,
    categoryId,
    suggestionType,
    variant.siteNavigation,
    variant.siteSubNavigation,
    variant.display,
    variant.product,
    addToBagError,
    addToBagMessage,
    device,
    wishlistMessage,
    wishlistError,
    notifyMe.display,
    product,
    variantsMap,
    handleFetchVariants,
    handleAddToCartTracking,
  ]);
  return {
    CartOperations,
    ctaCallbacks: {
      handleAddToCart: addToCartHandler, // this callback is paased to the sdk for handle add to cart
      wishlist: {
        handleWishList: addToWishlistHandler,
        wishlistData: wishlist,
      },
    },
  };
};

export default useCartOperations;
